import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { queryKeys, staleTimes } from '../config/queryClient';

/**
 * Hook para obtener categorías con productos paginados (carga progresiva)
 * 
 * Usa useInfiniteQuery para implementar infinite scroll:
 * - Primera carga: solo primeros productos (página 0)
 * - Scroll: carga más productos automáticamente (páginas siguientes)
 * 
 * @param {number} shopBranchId - ID de la sucursal
 * @param {number} categoryId - ID de categoría específica (opcional)
 * @param {number} pageSize - Tamaño de página (productos por categoría, default: 20)
 * @param {object} options - Opciones adicionales de React Query
 * @returns {object} - { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error }
 */
export const useCategoriesPaginated = (
  shopBranchId, 
  categoryId = null, 
  pageSize = 20,
  options = {}
) => {
  // Construir la URL del endpoint paginado
  // Reemplazar /cached/v1 por /paginated/v1 en la ruta base
  const baseUrl = process.env.REACT_APP_API_CATEGORIES_PRODUCTS_ROUTE_CACHED || 
                  '/api/categories/public/products/cached/v1';
  const paginatedUrl = baseUrl.replace('/cached/v1', '/paginated/v1');

  return useInfiniteQuery({
    queryKey: ['categoriesPaginated', shopBranchId, categoryId, pageSize],
    
    queryFn: async ({ pageParam = 0 }) => {
      const response = await axios.get(paginatedUrl, {
        params: {
          shopBranchId,
          page: pageParam,
          size: pageSize,
          categoryId: categoryId || undefined,
        },
      });
      return response.data; // Retorna PaginatedCategoriesResponse
    },
    
    getNextPageParam: (lastPage) => {
      // Si hay página siguiente, retornar el número de página siguiente
      return lastPage.hasNext ? lastPage.currentPage + 1 : undefined;
    },
    
    // Solo ejecutar si hay branchId
    enabled: !!shopBranchId,
    
    // Caché más corto para páginas siguientes (menos críticas)
    staleTime: (query) => {
      // Página 0 (primera): 5 minutos
      // Páginas siguientes: 10 minutos (menos críticas)
      return query.pageParam === 0 
        ? staleTimes.moderate // 10 minutos
        : staleTimes.long; // 30 minutos
    },
    
    // Mantener en caché por más tiempo (React Query v5: gcTime)
    gcTime: 30 * 60 * 1000, // 30 minutos
    retry: 2,
    refetchOnMount: true,

    // Opciones adicionales pasadas por el componente
    ...options,
  });
};

/**
 * Hook helper para aplanar todas las páginas en un solo array de categorías
 * Útil para componentes que necesitan todas las categorías cargadas hasta ahora
 * 
 * @param {object} infiniteQueryResult - Resultado de useCategoriesPaginated
 * @returns {Array} - Array plano de CategoryDTO con todos los productos cargados
 */
export const useFlattenedCategories = (infiniteQueryResult) => {
  return useMemo(() => {
    if (!infiniteQueryResult?.data?.pages) {
      return [];
    }

    // Agrupar categorías por ID (pueden aparecer en múltiples páginas)
    const categoriesMap = new Map();

    infiniteQueryResult.data.pages.forEach((page) => {
      if (page.categories && Array.isArray(page.categories)) {
        page.categories.forEach((category) => {
          if (category && category.id) {
            if (categoriesMap.has(category.id)) {
              // Si la categoría ya existe, agregar productos nuevos
              const existingCategory = categoriesMap.get(category.id);
              const existingProductIds = new Set(
                (existingCategory.productDTOList || []).map(p => p?.id).filter(Boolean)
              );
              
              // Agregar solo productos que no estén ya en la categoría
              const newProducts = (category.productDTOList || []).filter(
                product => product && product.id && !existingProductIds.has(product.id)
              );
              
              existingCategory.productDTOList = [
                ...(existingCategory.productDTOList || []),
                ...newProducts
              ];
            } else {
              // Nueva categoría, agregarla al mapa
              categoriesMap.set(category.id, {
                ...category,
                productDTOList: [...(category.productDTOList || [])]
              });
            }
          }
        });
      }
    });

    // Convertir mapa a array y ordenar por displayOrder
    return Array.from(categoriesMap.values())
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }, [infiniteQueryResult?.data?.pages]);
};

/**
 * Hook para invalidar el caché de categorías paginadas
 */
export const useInvalidateCategoriesPaginated = () => {
  const queryClient = useQueryClient();
  
  return (shopBranchId, categoryId = null) => {
    queryClient.invalidateQueries(['categoriesPaginated', shopBranchId, categoryId]);
  };
};
